// Audit & Compliance Center client (tenant plane). Talks to compliance-service
// via the gateway (/api/v1/compliance/audit/*), which enforces tenant scope +
// permission-gated field redaction server-side. Auth is the httpOnly session
// cookie + CSRF (gatewayApi) — no tenant is ever sent from the client.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1/compliance/audit';

export interface AuditActor {
  userId: string | null;
  actorType: string | null;
  agentName: string | null;
  onBehalfOf: string | null;
}

export interface AuditFeedEvent {
  id: string;
  occurredAt: string;
  action: string;
  module: string | null;
  subjectType: string | null;
  subjectId: string | null;
  channel: string | null;
  sensitivity: string | null;
  changedFields: string[];
  sourceService: string | null;
  actor: AuditActor;
}

export interface AuditDetail extends AuditFeedEvent {
  reason: string | null;
  correlationId: string | null;
  requestId: string | null;
  agentExecutionId: string | null;
  integrationName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  before: unknown;
  after: unknown;
  valuesRedacted: boolean;
}

export interface AuditFilters {
  from?: string;
  to?: string;
  module?: string;
  action?: string;
  channel?: string;
  sensitivity?: string;
  source?: string;
  subjectId?: string;
  actorUserId?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

function toParams(f: AuditFilters): Record<string, string> {
  const p: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== '') p[k] = String(v);
  }
  return p;
}

export interface AuditInvestigation {
  id: string;
  note: string | null;
  caseRef: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
}

export const auditService = {
  list: (f: AuditFilters) =>
    gatewayApi
      .get<{ data: AuditFeedEvent[]; nextCursor: string }>(`${BASE}/events`, { params: toParams(f) })
      .then((r) => r.data),

  detail: (id: string) => gatewayApi.get<AuditDetail>(`${BASE}/events/${id}`).then((r) => r.data),

  facets: () =>
    gatewayApi
      .get<{ modules: string[]; channels: string[]; sensitivities: string[] }>(`${BASE}/filters`)
      .then((r) => r.data),

  exportCsv: async (f: AuditFilters): Promise<Blob> => {
    const res = await gatewayApi.post(`${BASE}/export`, null, { params: toParams(f), responseType: 'blob' });
    return res.data as Blob;
  },

  investigations: (id: string) =>
    gatewayApi.get<{ data: AuditInvestigation[] }>(`${BASE}/events/${id}/investigations`).then((r) => r.data.data),

  addInvestigation: (id: string, body: { note?: string; caseRef?: string; status?: string }) =>
    gatewayApi.post(`${BASE}/events/${id}/investigation`, body).then((r) => r.data),
};
